import React, { useState, useEffect, useCallback, useRef, useContext, createContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import { sortSizeEntries } from '../utils/sizeOrder';
import usePageMeta from '../hooks/usePageMeta';
import {
  login,
  completeNewPassword,
  getIdToken,
  getValidIdToken,
  signOut,
  forgotPassword,
  confirmForgotPassword,
  beginMfaSetup,
  verifyMfaSetup,
  respondToMfaChallenge,
  selectMfaType,
} from '../utils/cognitoAuth';
import './Admin.css';

// === Themed confirm dialog (replaces native window.confirm) ===
//
// Usage:
//   const confirm = useConfirm();
//   if (!await confirm({ title, message, confirmLabel, danger })) return;

const ConfirmContext = createContext(() => Promise.resolve(false));
const useConfirm = () => useContext(ConfirmContext);

function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);
  const resolverRef = useRef(null);

  const confirm = useCallback((opts) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        title: opts.title || 'Are you sure?',
        message: opts.message || '',
        confirmLabel: opts.confirmLabel || 'Confirm',
        cancelLabel: opts.cancelLabel || 'Cancel',
        danger: !!opts.danger,
      });
    });
  }, []);

  const close = (value) => {
    setState(null);
    if (resolverRef.current) {
      const r = resolverRef.current;
      resolverRef.current = null;
      r(value);
    }
  };

  // Esc to cancel
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="confirm-overlay" onClick={() => close(false)} role="dialog" aria-modal="true">
          <div className="confirm-card" onClick={(e) => e.stopPropagation()}>
            <h3 className="confirm-title">{state.title}</h3>
            {state.message && <p className="confirm-message">{state.message}</p>}
            <div className="confirm-actions">
              <button type="button" className="btn-outline btn-small" onClick={() => close(false)}>
                {state.cancelLabel}
              </button>
              <button
                type="button"
                className={`btn-small ${state.danger ? 'btn-danger' : 'btn-pink'}`}
                autoFocus
                onClick={() => close(true)}
              >
                {state.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

// Tiny fetch helper. Pulls the Cognito ID token out of sessionStorage
// (set by the OAuth callback in cognitoAuth.js) and attaches it as a
// Bearer header. Backend's requireAdmin middleware verifies the
// signature against the user pool's JWKS and checks group membership.
//
// On 401/403 we throw "Unauthorized" so the caller can flip the UI
// back to the Sign-In screen. The Admin component watches for this
// and re-runs the login flow.
//
// The `token` option is accepted for backward compat with call sites
// that still pass it, but the value is ignored — the token always
// comes from sessionStorage.
async function api(path, { method = 'GET', body } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  const token = getIdToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(path, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 || res.status === 403) {
    throw new Error('Unauthorized');
  }

  let data = null;
  if (res.headers.get('content-type')?.includes('application/json')) {
    data = await res.json();
  }
  if (!res.ok) {
    throw new Error(data?.error || data?.message || `Request failed (${res.status})`);
  }
  return data;
}

function formatCents(cents) {
  return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// === Login screen ===
//
// Custom email + password form using Cognito's SRP API directly so
// we never bounce through the AWS hosted UI. Three sub-views:
//   - 'login'        → email + password, primary entry point
//   - 'newPassword'  → first-time login forces a real password to
//                       replace the temp one Cognito emailed
//   - 'forgot'       → request a reset code → enter code + new password
function LoginForm({ onSignedIn }) {
  const [view, setView] = useState('login');
  // The CognitoUser handle returned by login() when any challenge
  // fires (new password, MFA setup, MFA prompt). Held here so the
  // follow-up call can finish the flow against the same user.
  const [pendingUser, setPendingUser] = useState(null);

  // Login form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // New-password form state
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');

  // Forgot-password form state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotCode, setForgotCode] = useState('');
  const [forgotPw, setForgotPw] = useState('');
  const [forgotStage, setForgotStage] = useState('request'); // 'request' | 'confirm' | 'done'

  // MFA state. mfaCode = the 6-digit number the customer types.
  // mfaSetupInfo = { secretCode, otpAuthUri } returned by Cognito
  // during first-time enrollment so we can render the QR + secret.
  // mfaChallenge = the active challenge name (SOFTWARE_TOKEN_MFA or
  // SMS_MFA) so respondToMfaChallenge knows which response shape
  // Cognito expects. showSecret hides the long manual code by
  // default — most users scan the QR and never need it.
  const [mfaCode, setMfaCode] = useState('');
  const [mfaSetupInfo, setMfaSetupInfo] = useState(null);
  const [mfaChallenge, setMfaChallenge] = useState(null);
  const [showSecret, setShowSecret] = useState(false);

  // Routes the login() result to the right sub-view. Centralized so
  // every entry point (initial login, post-new-password, post-MFA-
  // selection) funnels through the same branch table.
  async function routeChallenge(result) {
    if (result.ok) {
      onSignedIn();
      return;
    }
    setPendingUser(result.user);
    if (result.challenge === 'NEW_PASSWORD_REQUIRED') {
      setView('newPassword');
    } else if (result.challenge === 'MFA_SETUP') {
      // Fetch the TOTP secret + QR uri so the setup view can render it.
      try {
        const info = await beginMfaSetup(result.user, email.trim() || 'admin');
        setMfaSetupInfo(info);
        setView('mfaSetup');
      } catch (err) {
        setError(friendlyAuthError(err));
      }
    } else if (result.challenge === 'SOFTWARE_TOKEN_MFA' || result.challenge === 'SMS_MFA') {
      setMfaChallenge(result.challenge);
      setView('mfaChallenge');
    } else if (result.challenge === 'SELECT_MFA_TYPE') {
      // Pool has multiple MFA methods enabled. Pick TOTP by default
      // since SMS isn't standard for us; if the future pool only
      // offers SMS, fall back to the first allowed method.
      const allowed = Array.isArray(result.allowed) ? result.allowed : [];
      const chosen = allowed.includes('SOFTWARE_TOKEN_MFA')
        ? 'SOFTWARE_TOKEN_MFA'
        : (allowed[0] || 'SOFTWARE_TOKEN_MFA');
      try {
        const next = await selectMfaType(result.user, chosen);
        await routeChallenge(next);
      } catch (err) {
        setError(friendlyAuthError(err));
      }
    } else {
      setError('Unexpected sign-in challenge: ' + (result.challenge || 'unknown'));
    }
  }

  async function handleLogin(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const result = await login(email.trim(), password);
      await routeChallenge(result);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // First-time TOTP enrollment: customer scanned the QR, app shows a
  // 6-digit code, they typed it. Verify with Cognito; on success
  // we're logged in. If verify fails (wrong code, typo), give them
  // another shot at the same form.
  async function handleMfaSetupVerify(e) {
    e.preventDefault();
    setError('');
    if (!pendingUser) {
      setError('Setup session expired — please sign in again.');
      setView('login');
      return;
    }
    setSubmitting(true);
    try {
      await verifyMfaSetup(pendingUser, mfaCode.trim());
      setMfaCode('');
      setMfaSetupInfo(null);
      onSignedIn();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // Existing-user MFA challenge: customer typed their app's code.
  async function handleMfaChallengeSubmit(e) {
    e.preventDefault();
    setError('');
    if (!pendingUser || !mfaChallenge) {
      setError('Session expired — please sign in again.');
      setView('login');
      return;
    }
    setSubmitting(true);
    try {
      await respondToMfaChallenge(pendingUser, mfaCode.trim(), mfaChallenge);
      setMfaCode('');
      setMfaChallenge(null);
      onSignedIn();
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSetNewPassword(e) {
    e.preventDefault();
    setError('');
    if (newPw !== newPw2) {
      setError('Passwords do not match');
      return;
    }
    setSubmitting(true);
    try {
      const result = await completeNewPassword(pendingUser, newPw);
      // completeNewPassword resolves { ok: true } today, but if MFA
      // is required Cognito surfaces the challenge AFTER the password
      // change. Re-use routeChallenge so MFA-enrollment-on-invite
      // flows hand off cleanly.
      await routeChallenge(result);
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgotRequest(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await forgotPassword(forgotEmail.trim());
      setForgotStage('confirm');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleForgotConfirm(e) {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      await confirmForgotPassword(forgotEmail.trim(), forgotCode.trim(), forgotPw);
      setForgotStage('done');
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Sub-view: MFA enrollment (first-time TOTP setup) ───────────────
  // Pool is set to required MFA and this user hasn't linked an
  // authenticator yet. Show the QR + secret, take the first 6-digit
  // code to confirm, then finish the sign-in.
  if (view === 'mfaSetup' && mfaSetupInfo) {
    // QR served by api.qrserver.com so we don't pull a QR npm dep.
    // Authenticator apps decode otpauth:// URIs directly so the
    // scan-friendly path is just "render this URI as a QR image."
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&margin=0&data=${encodeURIComponent(mfaSetupInfo.otpAuthUri)}`;
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <h1 className="script-font" style={{ marginBottom: 8 }}>Set up two-factor</h1>
          <p className="admin-login-sub" style={{ marginTop: 0, marginBottom: 24 }}>
            Scan this QR with an authenticator app (Google Authenticator, Authy, 1Password, etc.), then enter the 6-digit code it shows.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 24 }}>
            <div style={{ padding: 12, background: '#fff', border: '1px solid #eadcd5', borderRadius: 8 }}>
              <img src={qrSrc} alt="MFA setup QR code" width="220" height="220" style={{ display: 'block' }} />
            </div>
          </div>

          {/* Manual entry is the fallback for environments where
              scanning the QR doesn't work (shared screens, phone
              camera blocked, etc.). Hidden by default to keep the
              setup card visually focused on the QR. */}
          <div style={{ marginBottom: 24, textAlign: 'center' }}>
            {!showSecret ? (
              <button
                type="button"
                onClick={() => setShowSecret(true)}
                style={{
                  background: 'none', border: 'none', padding: 0,
                  color: '#b54a86', fontSize: 13, cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                Can't scan? Show manual code
              </button>
            ) : (
              <>
                <p className="admin-login-pw-hint" style={{ margin: '0 0 8px' }}>
                  Enter this code manually in your app:
                </p>
                <code style={{
                  display: 'block',
                  padding: '10px 12px',
                  background: '#f4eeec',
                  borderRadius: 6,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  wordBreak: 'break-all',
                  letterSpacing: '0.5px',
                  textAlign: 'center',
                }}>
                  {mfaSetupInfo.secretCode}
                </code>
                <button
                  type="button"
                  onClick={() => setShowSecret(false)}
                  style={{
                    marginTop: 6,
                    background: 'none', border: 'none', padding: 0,
                    color: '#888', fontSize: 12, cursor: 'pointer',
                    textDecoration: 'underline',
                  }}
                >
                  Hide
                </button>
              </>
            )}
          </div>

          <form onSubmit={handleMfaSetupVerify}>
            <label>6-digit code from your app</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              disabled={submitting}
              required
            />
            {error && <div className="admin-error" style={{ marginTop: 8 }}>{error}</div>}
            <button
              type="submit"
              className="btn-pink"
              disabled={submitting || mfaCode.length !== 6}
              style={{ marginTop: 16 }}
            >
              {submitting ? 'Verifying…' : 'Verify and continue'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Sub-view: MFA challenge (existing enrollment) ──────────────────
  // User has an authenticator already set up; just ask for the code.
  if (view === 'mfaChallenge') {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <h1 className="script-font">Two-factor code</h1>
          <p className="admin-login-sub">
            {mfaChallenge === 'SMS_MFA'
              ? 'Enter the code we just texted to your phone.'
              : 'Open your authenticator app and enter the 6-digit code for Heaven Sent.'}
          </p>
          <form onSubmit={handleMfaChallengeSubmit}>
            <label>Code</label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              autoFocus
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              disabled={submitting}
              required
            />
            {error && <div className="admin-error">{error}</div>}
            <button type="submit" className="btn-pink" disabled={submitting || mfaCode.length !== 6}>
              {submitting ? 'Verifying…' : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Sub-view: first-time password change ───────────────────────────
  if (view === 'newPassword') {
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <h1 className="script-font">Set your password</h1>
          <p className="admin-login-sub">
            Welcome to Heaven Sent. Choose a permanent password to finish setting up your account.
          </p>
          <form onSubmit={handleSetNewPassword}>
            <label>New password</label>
            <input
              type="password"
              autoFocus
              autoComplete="new-password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              disabled={submitting}
              required
              minLength={8}
            />
            <label>Confirm new password</label>
            <input
              type="password"
              autoComplete="new-password"
              value={newPw2}
              onChange={(e) => setNewPw2(e.target.value)}
              disabled={submitting}
              required
              minLength={8}
            />
            <p className="admin-login-pw-hint">
              At least 8 characters with upper + lower case, a number, and a symbol.
            </p>
            {error && <div className="admin-error">{error}</div>}
            <button type="submit" className="btn-pink" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ─── Sub-view: forgot password ──────────────────────────────────────
  if (view === 'forgot') {
    if (forgotStage === 'done') {
      return (
        <div className="admin-login">
          <div className="admin-login-card">
            <h1 className="script-font">Password updated</h1>
            <p className="admin-login-sub">
              Your password has been reset. You can sign in with the new password now.
            </p>
            <button
              type="button"
              className="btn-pink"
              onClick={() => {
                setView('login');
                setForgotStage('request');
                setError('');
              }}
            >
              Back to sign in
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="admin-login">
        <div className="admin-login-card">
          <h1 className="script-font">Reset password</h1>
          {forgotStage === 'request' ? (
            <form onSubmit={handleForgotRequest}>
              <p className="admin-login-sub">
                Enter your email and we'll send you a verification code.
              </p>
              <label>Email</label>
              <input
                type="email"
                autoFocus
                autoComplete="email"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                disabled={submitting}
                required
              />
              {error && <div className="admin-error">{error}</div>}
              <button type="submit" className="btn-pink" disabled={submitting}>
                {submitting ? 'Sending…' : 'Send code'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgotConfirm}>
              <p className="admin-login-sub">
                Check your email for a verification code from Cognito, then enter it here with your new password.
              </p>
              <label>Verification code</label>
              <input
                type="text"
                autoFocus
                inputMode="numeric"
                value={forgotCode}
                onChange={(e) => setForgotCode(e.target.value)}
                disabled={submitting}
                required
              />
              <label>New password</label>
              <input
                type="password"
                autoComplete="new-password"
                value={forgotPw}
                onChange={(e) => setForgotPw(e.target.value)}
                disabled={submitting}
                required
                minLength={8}
              />
              <p className="admin-login-pw-hint">
                At least 8 characters with upper + lower case, a number, and a symbol.
              </p>
              {error && <div className="admin-error">{error}</div>}
              <button type="submit" className="btn-pink" disabled={submitting}>
                {submitting ? 'Saving…' : 'Reset password'}
              </button>
            </form>
          )}
          <button
            type="button"
            className="admin-login-link"
            onClick={() => {
              setView('login');
              setForgotStage('request');
              setError('');
            }}
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    );
  }

  // ─── Default: email + password sign-in ──────────────────────────────
  return (
    <div className="admin-login">
      <div className="admin-login-card">
        <h1 className="script-font">Admin</h1>
        <p>Heaven Sent Promotions</p>
        <form onSubmit={handleLogin}>
          <label>Email</label>
          <input
            type="email"
            autoFocus
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            required
          />
          <label>Password</label>
          <input
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            required
          />
          {error && <div className="admin-error">{error}</div>}
          <button
            type="submit"
            className="btn-pink"
            disabled={submitting || !email || !password}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        <button
          type="button"
          className="admin-login-link"
          onClick={() => {
            setError('');
            setForgotEmail(email);
            setView('forgot');
          }}
        >
          Forgot password?
        </button>
      </div>
    </div>
  );
}

/* Map raw Cognito SDK errors to user-readable text. The SDK throws a
 * mix of error codes — most matter to debugging, only a few need
 * shown to a human. */
function friendlyAuthError(err) {
  if (!err) return 'Something went wrong. Please try again.';
  const code = err.code || err.name || '';
  const message = err.message || '';
  if (code === 'NotAuthorizedException') {
    if (/disabled/i.test(message)) return 'This account is disabled. Contact your account administrator.';
    return 'Email or password is incorrect.';
  }
  if (code === 'UserNotFoundException') return 'Email or password is incorrect.';
  if (code === 'PasswordResetRequiredException') return 'Your password has been reset. Use "Forgot password?" to set a new one.';
  if (code === 'UserNotConfirmedException') return 'Your email is not yet verified. Contact your account administrator.';
  if (code === 'CodeMismatchException') return 'That code is incorrect. Check your email and try again.';
  if (code === 'ExpiredCodeException') return 'That code has expired. Request a new one.';
  if (code === 'InvalidPasswordException') return message.replace(/^Password did not conform with policy: /, '') || 'Password does not meet the requirements.';
  if (code === 'LimitExceededException') return 'Too many attempts. Please wait a few minutes and try again.';
  return message || 'Something went wrong. Please try again.';
}

// === QuickBooks status panel ===
function QboPanel({ token }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api('/api/quickbooks/status', { token });
      setStatus(data);
    } catch {
      setStatus({ connected: false, configured: false });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  async function handleConnect() {
    // Guard against a second click while the first request is in flight.
    // Each call mints a new OAuth state, and a double-click used to leave
    // the user completing whichever Intuit page won the race.
    if (busy) return;
    setBusy(true);
    try {
      const { url } = await api('/api/quickbooks/auth', { token });
      window.location.href = url;
    } catch (err) {
      setBusy(false);
      alert(err.message || 'Failed to start QuickBooks connection');
    }
  }

  const confirm = useConfirm();
  async function handleDisconnect() {
    const ok = await confirm({
      title: 'Disconnect QuickBooks?',
      message: 'Existing estimates are not affected. You can reconnect anytime.',
      confirmLabel: 'Disconnect',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api('/api/quickbooks/disconnect', { method: 'POST', token });
      await reload();
    } catch (err) {
      alert(err.message || 'Failed to disconnect');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="qbo-panel">Loading QuickBooks status...</div>;

  return (
    <div className="qbo-panel">
      <div className="qbo-panel-row">
        <div>
          <strong>QuickBooks</strong>
          <div className="qbo-panel-meta">
            {status.configured ? `Environment: ${status.environment}` : 'Not configured (set QBO_CLIENT_ID etc.)'}
          </div>
        </div>
        <div className="qbo-panel-actions">
          {status.connected ? (
            <>
              <span className="qbo-badge qbo-badge-on">Connected</span>
              <button className="btn-outline btn-small" onClick={handleDisconnect} disabled={busy}>
                Disconnect
              </button>
            </>
          ) : (
            <>
              <span className="qbo-badge qbo-badge-off">Not connected</span>
              <button
                className="btn-pink btn-small"
                onClick={handleConnect}
                disabled={!status.configured || busy}
                title={status.configured ? '' : 'Backend missing QBO credentials'}
              >
                {busy ? 'Opening QuickBooks...' : 'Connect'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// Filename → previewable-as-an-img test (matches Quote.js)
function isImageFileName(name) {
  const ext = String(name || '').toLowerCase().split('.').pop();
  return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext);
}

function fileTypeLabel(name) {
  const ext = String(name || '').toLowerCase().split('.').pop();
  return ext ? ext.toUpperCase() : 'FILE';
}

// Lazy-loads a single branding-file blob via the auth-required GET.
// Streams locally in dev, follows S3 presigned URL in prod. Renders an
// <img> for image types, a small badge tile for everything else.
function BrandingFileTile({ file, token }) {
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isImageFileName(file.fileName)) return;
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    (async () => {
      try {
        const idToken = getIdToken();
        const res = await fetch(`/api/uploads/download/${file.fileKey}`, {
          headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.downloadUrl) throw new Error('No URL');
        const r2 = await fetch(data.downloadUrl);
        if (!r2.ok) throw new Error(`download HTTP ${r2.status}`);
        const blob = await r2.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setPreviewUrl(createdUrl);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Preview failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [file.fileKey, file.fileName, token]);

  const isImg = isImageFileName(file.fileName);
  return (
    <button
      type="button"
      className="branding-tile"
      onClick={() => downloadBrandingFile(file, token)}
      title={`Download ${file.fileName}`}
    >
      {isImg ? (
        previewUrl ? (
          <img className="branding-tile-img" src={previewUrl} alt={file.fileName} />
        ) : (
          <span className={`branding-tile-img branding-tile-placeholder${error ? ' is-error' : ''}`}>
            {error ? '!' : (loading ? '…' : '')}
          </span>
        )
      ) : (
        <span className="branding-tile-img branding-tile-badge">{fileTypeLabel(file.fileName)}</span>
      )}
      <span className="branding-tile-name">{file.fileName}</span>
    </button>
  );
}

// "Download all" button for the branding-files panel. Wraps
// downloadAllBrandingFiles with a busy state + progress label so
// Susan sees something is happening when the count is high.
function BrandingDownloadAllBtn({ stored, token }) {
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    setProgress({ done: 0, total: 0 });
    try {
      await downloadAllBrandingFiles(stored, token, (done, total) => {
        setProgress({ done, total });
      });
    } finally {
      setBusy(false);
      setProgress({ done: 0, total: 0 });
    }
  };
  return (
    <button
      type="button"
      className="branding-download-all"
      onClick={onClick}
      disabled={busy}
      title="Download every logo file attached to this quote"
    >
      {busy
        ? `Downloading ${progress.done}/${progress.total}…`
        : 'Download all'}
    </button>
  );
}

// === Quote row ===
function QuoteRow({ quote, token, onChange }) {
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const confirm = useConfirm();

  async function handleSendToQbo() {
    const ok = await confirm({
      title: `Send ${quote.quote_number} to QuickBooks?`,
      message: `This will create an Estimate in QuickBooks for ${quote.customer_name}.`,
      confirmLabel: 'Send to QBO',
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api('/api/quickbooks/estimate', {
        method: 'POST',
        token,
        body: { quoteId: quote.id },
      });
      onChange();
    } catch (err) {
      alert(err.message || 'Failed to send to QuickBooks');
    } finally {
      setBusy(false);
    }
  }

  async function handleArchive() {
    const ok = await confirm({
      title: `Archive ${quote.quote_number}?`,
      message: 'Archived quotes are hidden from the New tab and excluded from the default sales export.',
      confirmLabel: 'Archive',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/quotes/admin/${quote.id}/status`, {
        method: 'PUT',
        token,
        body: { status: 'archived' },
      });
      onChange();
    } catch (err) {
      alert(err.message || 'Failed to archive');
    } finally {
      setBusy(false);
    }
  }

  const items = Array.isArray(quote.items) ? quote.items : [];

  return (
    <>
      <tr className={`quote-row quote-row-${quote.status}${expanded ? ' is-expanded' : ''}`}>
        <td className="quote-expand-cell">
          <button
            type="button"
            className="quote-expand-btn"
            onClick={() => setExpanded(e => !e)}
            aria-label={expanded ? 'Collapse details' : 'Expand details'}
            aria-expanded={expanded}
          >
            <span className={`expand-caret${expanded ? ' is-open' : ''}`}>&#9656;</span>
          </button>
        </td>
        <td className="quote-num">{quote.quote_number}</td>
        <td>
          <div>{quote.customer_name}</div>
          <div className="quote-sub">{quote.customer_email}</div>
          {quote.company && <div className="quote-sub">{quote.company}</div>}
        </td>
        <td className="quote-total">{formatCents(quote.total_cents)}</td>
        <td>
          <span className={`status-pill status-${quote.status}`}>{quote.status.replace('_', ' ')}</span>
          {quote.qbo_estimate_doc_number && (
            <div className="quote-sub">QBO #{quote.qbo_estimate_doc_number}</div>
          )}
        </td>
        <td className="quote-date">{formatDate(quote.created_at)}</td>
        <td className="quote-actions">
          {quote.status === 'new' && (
            <button className="btn-pink btn-small" disabled={busy} onClick={handleSendToQbo}>
              {busy ? '...' : 'Send to QBO'}
            </button>
          )}
          {quote.status !== 'archived' && (
            <button className="btn-outline btn-small" disabled={busy} onClick={handleArchive}>
              Archive
            </button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="quote-detail-row">
          <td colSpan={7}>
            <div className="quote-detail">
              <div className="quote-detail-toolbar">
                <button
                  type="button"
                  className="btn-pink btn-small"
                  onClick={() => downloadWorkOrder(quote, token)}
                  title="Download a printable work order to hand off to production"
                >
                  Download work order (PDF)
                </button>
              </div>
              <div className="quote-detail-meta">
                {quote.customer_phone && <div><strong>Phone:</strong> {quote.customer_phone}</div>}
                {quote.deadline && <div><strong>Deadline:</strong> {new Date(quote.deadline).toLocaleDateString()}</div>}
                {quote.budget && <div><strong>Budget:</strong> {quote.budget}</div>}
              </div>
              {quote.description && (
                <div className="quote-detail-notes">
                  <strong>Customer notes</strong>
                  <p>{quote.description}</p>
                </div>
              )}

              <h4 className="quote-detail-heading">Items ({items.length})</h4>
              {items.length === 0 ? (
                <div className="quote-detail-empty">No item data on this quote.</div>
              ) : (
                <table className="quote-items-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Color</th>
                      <th>Sizes</th>
                      <th>Qty</th>
                      <th>Decoration</th>
                      <th>Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => {
                      const sizes = it.sizes || {};
                      const sizeBreakdown = sortSizeEntries(Object.entries(sizes))
                        .filter(([, n]) => Number(n) > 0)
                        .map(([s, n]) => `${s}: ${n}`)
                        .join(', ');
                      const qty = it.totalQty ?? Object.values(sizes).reduce((s, n) => s + Number(n || 0), 0);
                      const lineTotal = it.pricing?.totalCents ?? it.pricing?.total ?? null;
                      const decoration = it.decoration && it.decoration !== 'None' ? it.decoration : '—';
                      return (
                        <tr key={`${it.styleNumber}-${i}`}>
                          <td>
                            <div className="item-name">{it.name}</div>
                            <div className="item-sub">#{it.styleNumber} &middot; {it.brand}</div>
                          </td>
                          <td>
                            <div className="item-color">
                              <span className="color-swatch" style={{ background: it.colorHex || '#ccc' }} />
                              {it.color}
                            </div>
                          </td>
                          <td className="item-sizes">{sizeBreakdown || '—'}</td>
                          <td className="item-qty">{qty}</td>
                          <td>{decoration}</td>
                          <td className="item-total">{lineTotal != null ? formatCents(lineTotal) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              <div className="quote-detail-totals">
                <div><span>Subtotal</span><span>{formatCents(quote.subtotal_cents)}</span></div>
                <div><span>Setup fees</span><span>{formatCents(quote.setup_fees_cents)}</span></div>
                <div className="grand"><span>Total</span><span>{formatCents(quote.total_cents)}</span></div>
              </div>

              {(() => {
                // Two shapes possible (kept for back-compat):
                //   Legacy: flat array  [{ fileName, fileKey }, ...]
                //   New:    keyed object { front: [...], back: [...], leftSleeve: [...], rightSleeve: [...], other: [...] }
                //
                // The keyed shape is a legacy artifact of when each
                // side carried its own design. With the logos-as-
                // entities model a logo can be applied to any side of
                // any item, so labelling files "Front Design" / "Back
                // Design" is misleading. We render the design uploads
                // numbered as Logo 1 / Logo 2 / ... in upload order
                // and keep "Other" for reference materials.
                const stored = quote.branding_files;
                const DESIGN_SIDES = ['front', 'back', 'leftSleeve', 'rightSleeve'];
                if (Array.isArray(stored) && stored.length > 0) {
                  return (
                    <div className="quote-detail-files">
                      <div className="branding-files-header">
                        <strong>Branding files ({stored.length})</strong>
                        <BrandingDownloadAllBtn stored={stored} token={token} />
                      </div>
                      <div className="branding-tile-grid">
                        {stored.map(f => (
                          <BrandingFileTile key={f.fileKey} file={f} token={token} />
                        ))}
                      </div>
                    </div>
                  );
                }
                if (stored && typeof stored === 'object') {
                  // Flatten design-side files into a single ordered list
                  // and render each as "Logo N". Reference material in
                  // "other" stays separate.
                  const designFiles = [];
                  for (const side of DESIGN_SIDES) {
                    const list = Array.isArray(stored[side]) ? stored[side] : [];
                    for (const f of list) designFiles.push(f);
                  }
                  const otherFiles = Array.isArray(stored.other) ? stored.other : [];
                  const total = designFiles.length + otherFiles.length;
                  if (total === 0) return null;
                  return (
                    <div className="quote-detail-files">
                      <div className="branding-files-header">
                        <strong>Branding files ({total})</strong>
                        <BrandingDownloadAllBtn stored={stored} token={token} />
                      </div>
                      {designFiles.map((f, i) => (
                        <div key={f.fileKey || i} className="branding-side-group">
                          <div className="branding-side-label">Logo {i + 1}</div>
                          <div className="branding-tile-grid">
                            <BrandingFileTile file={f} token={token} />
                          </div>
                        </div>
                      ))}
                      {otherFiles.length > 0 && (
                        <div className="branding-side-group">
                          <div className="branding-side-label">Other Files</div>
                          <div className="branding-tile-grid">
                            {otherFiles.map(f => (
                              <BrandingFileTile key={f.fileKey} file={f} token={token} />
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                return null;
              })()}

              {quote.internal_notes && (
                <div className="quote-detail-notes">
                  <strong>Internal notes</strong>
                  <p>{quote.internal_notes}</p>
                </div>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// Download a printable per-quote work order PDF. Susan hands these off
// to her production team as the spec / pull-sheet for each order.
async function downloadWorkOrder(quote /* token: deprecated, ignored */) {
  try {
    const idToken = getIdToken();
    const res = await fetch(`/api/quotes/admin/${quote.id}/work-order`, {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const blob = await res.blob();
    const cd = res.headers.get('content-disposition') || '';
    const filename =
      (cd.match(/filename="?([^"]+)"?/) || [])[1] ||
      `heaven-sent-${quote.quote_number}-work-order.pdf`;
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    alert(err.message || 'Could not download work order');
  }
}

// Download a branding file: backend returns a short-lived presigned URL,
// browser opens it (works against AWS S3 in prod, MinIO in dev).
async function downloadBrandingFile(file /* token: deprecated, ignored */) {
  try {
    const idToken = getIdToken();
    const res = await fetch(`/api/uploads/download/${file.fileKey}`, {
      headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    const data = await res.json();
    if (!data.downloadUrl) throw new Error('No download URL');
    window.open(data.downloadUrl, '_blank', 'noopener');
  } catch (err) {
    alert(err.message || 'Could not download file');
  }
}

// Download every branding file attached to a quote in one go. Handles
// both shapes (legacy flat array, new per-side object). Each file
// is fetched as a blob and saved with its original filename so
// Susan ends up with a clean folder of named artwork files.
async function downloadAllBrandingFiles(stored, _tokenIgnored, onProgress) {
  const all = [];
  if (Array.isArray(stored)) {
    for (const f of stored) if (f && f.fileKey) all.push(f);
  } else if (stored && typeof stored === 'object') {
    for (const side of ['front', 'back', 'leftSleeve', 'rightSleeve', 'other']) {
      const list = Array.isArray(stored[side]) ? stored[side] : [];
      for (const f of list) if (f && f.fileKey) all.push(f);
    }
  }
  if (all.length === 0) return;
  const errors = [];
  for (let i = 0; i < all.length; i++) {
    const file = all[i];
    if (onProgress) onProgress(i + 1, all.length, file.fileName);
    try {
      const idToken = getIdToken();
      const res = await fetch(`/api/uploads/download/${file.fileKey}`, {
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      if (!data.downloadUrl) throw new Error('No download URL');
      const blobRes = await fetch(data.downloadUrl);
      if (!blobRes.ok) throw new Error(`download HTTP ${blobRes.status}`);
      const blob = await blobRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.fileName || `logo-${i + 1}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so the browser has time to start
      // the download. Immediate revocation can race the save dialog
      // on some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      errors.push(`${file.fileName}: ${err.message}`);
    }
  }
  if (errors.length > 0) {
    alert(`Some files failed to download:\n${errors.join('\n')}`);
  }
}

// === Quotes table ===
function QuotesTable({ token }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('new');
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState('');
  const [exportFormat, setExportFormat] = useState('pdf');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const data = await api(`/api/quotes/admin${params}`, { token });
      setQuotes(data.quotes);
    } catch (err) {
      setError(err.message || 'Failed to load quotes');
    } finally {
      setLoading(false);
    }
  }, [token, filter]);

  useEffect(() => { load(); }, [load]);

  // Sales export download (PDF default, CSV alternative). Auth header
  // means we can't just use a plain <a href>; fetch as a blob and trigger
  // a synthetic download click.
  async function downloadExport(period) {
    setExporting(period);
    try {
      const idToken = getIdToken();
      const res = await fetch(`/api/quotes/admin/export?period=${period}&format=${exportFormat}`, {
        headers: idToken ? { Authorization: `Bearer ${idToken}` } : {},
      });
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const blob = await res.blob();
      const cd = res.headers.get('content-disposition') || '';
      const filename =
        (cd.match(/filename="?([^"]+)"?/) || [])[1] ||
        `heaven-sent-sales-${period}.${exportFormat}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting('');
    }
  }

  return (
    <div className="quotes-section">
      <div className="quotes-header">
        <h2>Quotes</h2>
        <div className="quotes-filter">
          {['new', 'sent_to_qbo', 'archived', 'all'].map(f => (
            <button
              key={f}
              className={`filter-pill ${filter === f ? 'active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'sent_to_qbo' ? 'Sent to QBO' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="quotes-toolbar">
        <span className="quotes-toolbar-label">Export sales:</span>
        {/* Quotes auto-purge from the database after 30 days, so we
            cap the export window at "Last month" — anything older
            simply isn't there to export. */}
        {['week', 'month'].map(p => (
          <button
            key={p}
            type="button"
            className="btn-outline btn-small"
            disabled={!!exporting}
            onClick={() => downloadExport(p)}
          >
            {exporting === p ? 'Preparing…' : `Last ${p}`}
          </button>
        ))}
        <span className="export-format-toggle" role="group" aria-label="Export format">
          {['pdf', 'csv'].map(f => (
            <button
              key={f}
              type="button"
              className={`format-pill${exportFormat === f ? ' is-active' : ''}`}
              onClick={() => setExportFormat(f)}
              disabled={!!exporting}
            >
              {f.toUpperCase()}
            </button>
          ))}
        </span>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="quotes-empty">Loading...</div>
      ) : quotes.length === 0 ? (
        <div className="quotes-empty">No quotes in this view yet.</div>
      ) : (
        <table className="quotes-table">
          <thead>
            <tr>
              <th aria-label="Expand"></th>
              <th>#</th>
              <th>Customer</th>
              <th>Total</th>
              <th>Status</th>
              <th>Submitted</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {quotes.map(q => (
              <QuoteRow key={q.id} quote={q} token={token} onChange={load} />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// === Page wrapper ===
// === Staged ecommerce orders ===
//
// Orders pushed in from connected storefronts (RHA today). They arrive
// already paid and wait here until someone sends them to QuickBooks, so
// this table is a work queue rather than a record: the default view is
// "New", and the point of the page is to empty it.

const ORDER_STATUS_LABELS = {
  new: 'New',
  sent_to_qbo: 'Sent to QBO',
  archived: 'Archived',
  all: 'All',
};

function StagedOrderRow({ order, expanded, onToggleExpand, selectable, selected, onToggleSelect, onChange }) {
  const [busy, setBusy] = useState(false);
  const confirm = useConfirm();

  async function handleSend() {
    const ok = await confirm({
      title: `Send ${order.sourceOrderNumber} to QuickBooks?`,
      message: `This will create a${order.qboDocType === 'invoice' ? 'n Invoice' : 'n Estimate'} for ${order.customerName || order.customerEmail || 'this customer'}.`,
      confirmLabel: 'Send to QBO',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const data = await api('/api/quickbooks/staged-invoice', {
        method: 'POST',
        body: { ids: [order.id] },
      });
      const outcome = data.results && data.results[0];
      if (!outcome || !outcome.ok) {
        alert(outcome?.error || 'Failed to send to QuickBooks');
      }
      onChange();
    } catch (err) {
      alert(err.message || 'Failed to send to QuickBooks');
    } finally {
      setBusy(false);
    }
  }

  async function handleArchive() {
    const ok = await confirm({
      title: `Archive ${order.sourceOrderNumber}?`,
      message: 'Archived orders are hidden from the New tab. They are never auto-deleted.',
      confirmLabel: 'Archive',
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api(`/api/staged-orders/${order.id}`, { method: 'PATCH', body: { status: 'archived' } });
      onChange();
    } catch (err) {
      alert(err.message || 'Failed to archive');
    } finally {
      setBusy(false);
    }
  }

  const totalUnits = (order.items || []).reduce((sum, i) => sum + (i.quantity || 0), 0);
  const addr = order.shippingAddress || {};
  const hasAddress = Object.keys(addr).length > 0;

  return (
    <>
      <tr className="quote-row">
        <td className="order-select-cell">
          {selectable && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect(order.id)}
              aria-label={`Select order ${order.sourceOrderNumber}`}
            />
          )}
        </td>
        <td className="quote-expand-cell">
          <button
            className="quote-expand-btn"
            onClick={onToggleExpand}
            aria-expanded={expanded}
            aria-label={expanded ? 'Collapse order' : 'Expand order'}
          >
            {expanded ? '▾' : '▸'}
          </button>
        </td>
        <td><span className="site-tag">{order.sourceLabel}</span></td>
        <td>{order.sourceOrderNumber}</td>
        <td>{order.customerName || order.customerEmail || '—'}</td>
        <td>{totalUnits}</td>
        <td>{formatCents(order.totalCents)}</td>
        <td>{formatDate(order.placedAt || order.createdAt)}</td>
        <td>
          <span className={`status-pill status-${order.status}`}>
            {ORDER_STATUS_LABELS[order.status] || order.status}
          </span>
        </td>
      </tr>

      {expanded && (
        <tr className="quote-detail-row">
          <td colSpan={9}>
            <div className="quote-detail">
              <div className="quote-detail-meta">
                <div>
                  <strong>Customer</strong>
                  <div>{order.customerName || '—'}</div>
                  {order.customerEmail && <div>{order.customerEmail}</div>}
                  {order.customerPhone && <div>{order.customerPhone}</div>}
                </div>
                <div>
                  <strong>Ship to</strong>
                  {hasAddress ? (
                    <div className="order-address">
                      {[addr.line1, addr.line2].filter(Boolean).map((l, i) => <div key={i}>{l}</div>)}
                      <div>{[addr.city, addr.state, addr.postalCode].filter(Boolean).join(', ')}</div>
                      {addr.country && <div>{addr.country}</div>}
                    </div>
                  ) : (
                    <div className="muted">Not provided</div>
                  )}
                </div>
                <div>
                  <strong>Source</strong>
                  <div>{order.sourceLabel}</div>
                  <div className="muted">{order.sourceSite}</div>
                </div>
                {(order.qboInvoiceDocNumber || order.qboEstimateDocNumber) && (
                  <div>
                    <strong>QuickBooks</strong>
                    {/* Orders sent before the switch to Invoices carry an
                        estimate number instead. Label each for what it is. */}
                    <div>
                      {order.qboInvoiceDocNumber
                        ? `Invoice #${order.qboInvoiceDocNumber}`
                        : `Estimate #${order.qboEstimateDocNumber}`}
                    </div>
                    <div className="muted">{formatDate(order.qboSentAt)}</div>
                  </div>
                )}
              </div>

              <table className="quote-items-table">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Style</th>
                    <th>Color</th>
                    <th>Size</th>
                    <th>Qty</th>
                    <th>Unit</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.items || []).map(item => (
                    <tr key={item.id}>
                      <td>
                        {item.productTitle}
                        {item.brandName && <span className="muted"> · {item.brandName}</span>}
                        {item.customization && <div className="muted">{item.customization}</div>}
                      </td>
                      <td>{item.styleNumber || '—'}</td>
                      <td>{item.color || '—'}</td>
                      <td>{item.size || '—'}</td>
                      <td>{item.quantity}</td>
                      <td>{formatCents(item.unitPriceCents)}</td>
                      <td>{formatCents(item.unitPriceCents * item.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={6}><strong>Order total</strong></td>
                    <td><strong>{formatCents(order.totalCents)}</strong></td>
                  </tr>
                </tfoot>
              </table>

              {order.internalNotes && (
                <div className="quote-detail-notes"><strong>Notes</strong><p>{order.internalNotes}</p></div>
              )}

              <div className="quote-detail-toolbar">
                {order.status === 'new' && (
                  <>
                    <button className="btn-pink btn-small" onClick={handleSend} disabled={busy}>
                      {busy ? 'Sending…' : 'Send to QuickBooks'}
                    </button>
                    <button className="btn-outline btn-small" onClick={handleArchive} disabled={busy}>
                      Archive
                    </button>
                  </>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function OrdersTable() {
  const [orders, setOrders] = useState([]);
  const [sites, setSites] = useState([]);
  const [site, setSite] = useState('all');
  const [status, setStatus] = useState('new');
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [sendSummary, setSendSummary] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (site !== 'all') params.set('site', site);
      if (status !== 'all') params.set('status', status);
      const qs = params.toString();
      const [rows, siteRows] = await Promise.all([
        api(`/api/staged-orders${qs ? `?${qs}` : ''}`),
        api('/api/staged-orders/sites'),
      ]);
      setOrders(rows || []);
      setSites(siteRows || []);
    } catch (err) {
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [site, status]);

  useEffect(() => { load(); }, [load]);

  // Drop any selection that is no longer on screen. Without this, changing
  // the filter and hitting Send would push orders the operator can't see.
  useEffect(() => {
    setSelected(prev => {
      const visible = new Set(orders.map(o => o.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [orders]);

  const sendable = orders.filter(o => o.status === 'new');
  const allSendableSelected = sendable.length > 0 && sendable.every(o => selected.has(o.id));

  function toggleSelect(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(allSendableSelected ? new Set() : new Set(sendable.map(o => o.id)));
  }

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleSendSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    // A selection can mix storefronts that take payment with ones that do
    // not, so spell out the split rather than claiming one document type.
    const picked = sendable.filter(o => selected.has(o.id));
    const invoices = picked.filter(o => o.qboDocType === 'invoice').length;
    const estimates = picked.length - invoices;
    const breakdown = [
      invoices ? `${invoices} paid order${invoices === 1 ? '' : 's'} become${invoices === 1 ? 's' : ''} an Invoice` : null,
      estimates ? `${estimates} unpaid order${estimates === 1 ? '' : 's'} become${estimates === 1 ? 's' : ''} an Estimate` : null,
    ].filter(Boolean).join('; ');
    const ok = await confirm({
      title: `Send ${ids.length} order${ids.length === 1 ? '' : 's'} to QuickBooks?`,
      message: `${breakdown}. Orders that fail are left untouched so you can retry them.`,
      confirmLabel: 'Send to QBO',
    });
    if (!ok) return;
    setSending(true);
    setSendSummary(null);
    try {
      const data = await api('/api/quickbooks/staged-invoice', { method: 'POST', body: { ids } });
      setSendSummary(data);
      setSelected(new Set());
      await load();
    } catch (err) {
      setError(err.message || 'Failed to send orders');
    } finally {
      setSending(false);
    }
  }

  const failures = (sendSummary?.results || []).filter(r => !r.ok);

  return (
    <div className="quotes-section">
      <div className="quotes-header">
        <h2>Orders</h2>
        <div className="quotes-filter">
          {['new', 'sent_to_qbo', 'archived', 'all'].map(f => (
            <button
              key={f}
              className={`filter-pill ${status === f ? 'active' : ''}`}
              onClick={() => setStatus(f)}
            >
              {ORDER_STATUS_LABELS[f]}
            </button>
          ))}
        </div>
      </div>

      {/* Site pills come from the API, so a new storefront appears here on
          its first order with no frontend change. */}
      <div className="quotes-filter site-filter">
        <button
          className={`filter-pill ${site === 'all' ? 'active' : ''}`}
          onClick={() => setSite('all')}
        >
          All sites
        </button>
        {sites.map(s => (
          <button
            key={s.sourceSite}
            className={`filter-pill ${site === s.sourceSite ? 'active' : ''}`}
            onClick={() => setSite(s.sourceSite)}
          >
            {s.sourceLabel}
            {s.newCount > 0 && <span className="pill-count">{s.newCount}</span>}
          </button>
        ))}
      </div>

      {selected.size > 0 && (
        <div className="orders-bulk-bar">
          <span>{selected.size} selected</span>
          <button className="btn-pink btn-small" onClick={handleSendSelected} disabled={sending}>
            {sending ? 'Sending…' : `Send ${selected.size} to QuickBooks`}
          </button>
          <button className="btn-outline btn-small" onClick={() => setSelected(new Set())} disabled={sending}>
            Clear
          </button>
        </div>
      )}

      {sendSummary && (
        <div className={`admin-toast ${failures.length ? 'admin-toast-warn' : ''}`}>
          Sent {sendSummary.sent} to QuickBooks
          {failures.length > 0 && (
            <>
              , {failures.length} failed:
              <ul className="orders-failures">
                {failures.map(f => <li key={f.id}>Order {f.id}: {f.error}</li>)}
              </ul>
            </>
          )}
        </div>
      )}

      {error && <div className="admin-error">{error}</div>}

      {loading ? (
        <div className="admin-loading">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="quotes-empty">No orders here.</div>
      ) : (
        <table className="quotes-table">
          <thead>
            <tr>
              <th className="order-select-cell">
                {sendable.length > 0 && (
                  <input
                    type="checkbox"
                    checked={allSendableSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all sendable orders"
                  />
                )}
              </th>
              <th />
              <th>Site</th>
              <th>Order</th>
              <th>Customer</th>
              <th>Units</th>
              <th>Total</th>
              <th>Placed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {orders.map(order => (
              <StagedOrderRow
                key={order.id}
                order={order}
                expanded={expanded.has(order.id)}
                onToggleExpand={() => toggleExpand(order.id)}
                selectable={order.status === 'new'}
                selected={selected.has(order.id)}
                onToggleSelect={toggleSelect}
                onChange={load}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// === HeavenSent Studio desktop app downloads ===
//
// Installers live in a private S3 bucket. The backend checks the signed-in
// session and hands back a link that expires in a few minutes, so a copied
// link is useless to anyone who is not logged in here.

function formatFileSize(bytes) {
  if (!bytes) return '';
  return `${Math.round(bytes / (1024 * 1024))} MB`;
}

function DesktopAppPanel() {
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    let cancelled = false;
    api('/api/desktop-app')
      .then(data => { if (!cancelled) setInfo(data); })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not load downloads'); });
    return () => { cancelled = true; };
  }, []);

  async function download(platform) {
    setBusy(platform);
    setError('');
    try {
      const data = await api(`/api/desktop-app/download/${platform}`);
      window.location.assign(data.downloadUrl);
    } catch (e) {
      setError(e.message || 'Download failed');
    } finally {
      setBusy('');
    }
  }

  if (!info && !error) {
    return <div className="desktop-app-panel">Loading downloads…</div>;
  }

  return (
    <section className="desktop-app-panel">
      <div className="desktop-app-head">
        <div>
          <strong>HeavenSent Studio</strong>
          <div className="desktop-app-meta">
            Embroidery digitizing app for Windows and Mac.
            {info?.version && ` Version ${info.version}`}
            {info?.publishedAt && `, released ${new Date(info.publishedAt).toLocaleDateString()}`}
          </div>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {info && !info.available && (
        <p className="desktop-app-meta">No release has been published yet.</p>
      )}

      {info?.available && (
        <ul className="desktop-app-list">
          {info.files.map(f => (
            <li key={f.platform} className="desktop-app-row">
              <div>
                <div className="desktop-app-label">{f.label}</div>
                <div className="desktop-app-meta">{f.fileName}{f.size ? ` · ${formatFileSize(f.size)}` : ''}</div>
              </div>
              <button
                type="button"
                className="btn-pink btn-small"
                disabled={!!busy}
                onClick={() => download(f.platform)}
              >
                {busy === f.platform ? 'Preparing…' : 'Download'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {info?.available && (
        <details className="desktop-app-help">
          <summary>Installing</summary>
          <p>
            <strong>Windows:</strong> run the installer. If Windows says it protected your PC,
            click <em>More info</em>, then <em>Run anyway</em>.
          </p>
          <p>
            <strong>Mac:</strong> open the download and drag HeavenSent Studio into Applications.
            The first time, right-click the app and choose <em>Open</em>. Use the Apple chip
            version for M1 and newer Macs, Intel for older ones.
          </p>
        </details>
      )}
    </section>
  );
}

function Admin() {
  // Tell crawlers to skip this page so it never appears in search
  // results. The robots.txt at the public root also disallows /manage,
  // but the meta tag is a stronger signal that survives even if
  // someone forgets robots.txt on a future domain migration. Bots
  // that ignore both still hit Cognito's login screen with no
  // credentials — that's the actual security boundary.
  usePageMeta({
    title: 'Admin',
    description: '',
    noindex: true,
  });

  // Tri-state: 'checking' (verifying token), 'authed', or 'guest'.
  // We start in 'checking' so the page doesn't flash the login form
  // while we're confirming the cached session is still valid.
  const [authState, setAuthState] = useState('checking');
  const [searchParams] = useSearchParams();
  // Quotes and orders are separate workflows, so they get separate tabs
  // rather than stacking into one long scroll. Quotes stays the landing
  // tab because it is the older, higher-volume queue.
  const [tab, setTab] = useState('quotes');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Try to use the cached Cognito session. getValidIdToken auto-
      // refreshes if the access token has expired but the refresh
      // token is still good, so an admin who closed the tab a few
      // hours ago doesn't have to re-enter their password.
      const token = await getValidIdToken();
      if (!token) {
        if (!cancelled) setAuthState('guest');
        return;
      }
      // Confirm the token still authorizes against the backend (group
      // membership + key rotation safety). If /me fails, force a re-login.
      try {
        await api('/api/admin/me');
        if (!cancelled) setAuthState('authed');
      } catch {
        if (!cancelled) setAuthState('guest');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function handleLogout() {
    setAuthState('guest');
    await signOut();
  }

  if (authState === 'checking') {
    return <div className="admin-page admin-loading">Checking session…</div>;
  }
  if (authState === 'guest') {
    return <LoginForm onSignedIn={() => setAuthState('authed')} />;
  }

  return (
    <ConfirmProvider>
      <main className="admin-page">
        <header className="admin-header">
          <h1 className="script-font">Admin Dashboard</h1>
          <button className="btn-outline btn-small" onClick={handleLogout}>Sign out</button>
        </header>

        {searchParams.get('qbo') === 'connected' && (
          <div className="admin-toast">QuickBooks connected.</div>
        )}
        {searchParams.get('qbo') === 'state_expired' && (
          <div className="admin-toast admin-toast-warn">
            That QuickBooks connection attempt timed out or was started in another
            tab. Click Connect to try again.
          </div>
        )}
        {searchParams.get('qbo') === 'error' && (
          <div className="admin-toast admin-toast-warn">
            QuickBooks did not finish connecting. Click Connect to try again.
          </div>
        )}

        <QboPanel />

        <nav className="admin-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={tab === 'quotes'}
            className={`admin-tab ${tab === 'quotes' ? 'active' : ''}`}
            onClick={() => setTab('quotes')}
          >
            Quotes
          </button>
          <button
            role="tab"
            aria-selected={tab === 'orders'}
            className={`admin-tab ${tab === 'orders' ? 'active' : ''}`}
            onClick={() => setTab('orders')}
          >
            Orders
          </button>
          <button
            role="tab"
            aria-selected={tab === 'desktop-app'}
            className={`admin-tab ${tab === 'desktop-app' ? 'active' : ''}`}
            onClick={() => setTab('desktop-app')}
          >
            Desktop App
          </button>
        </nav>

        {tab === 'quotes' && <QuotesTable />}
        {tab === 'orders' && <OrdersTable />}
        {tab === 'desktop-app' && <DesktopAppPanel />}
      </main>
    </ConfirmProvider>
  );
}

export default Admin;
